import { useCallback, useRef, useState } from 'react'

import type { FunkitConfig } from '../identity/IdentityContext'
import { logger } from '../logging/logger'

interface IdentityProps {
  config: FunkitConfig
  walletAddress: string | undefined
}

/**
 * Identity holds still while a flow is on screen: swapping the user under an
 * open KYC or quote is undefined behaviour, so the props a flow opened with
 * stay in force until it closes. A change that lands mid-flow is logged and
 * applied on close — or when the next flow opens, replacement included, so a
 * flow never pays as the user before it. Appearance and locale stay live;
 * only who is paying does not.
 */
export function useFrozenIdentity(live: IdentityProps): {
  identity: IdentityProps
  onFlowOpenChange: (isOpen: boolean) => void
} {
  const latest = useRef(live)
  latest.current = live
  const [frozen, setFrozen] = useState<IdentityProps>()
  const warned = useRef(false)

  // Every open re-freezes, not only the closed→open edge: the flow provider
  // reports a replacement as another open, and that flow is a new session.
  const onFlowOpenChange = useCallback((isOpen: boolean) => {
    warned.current = false
    setFrozen(isOpen ? latest.current : undefined)
  }, [])

  if (frozen !== undefined && !sameIdentity(frozen, live) && !warned.current) {
    warned.current = true
    logger.warn('connect-rn:identity_changed_mid_flow', {
      fromUserId: frozen.config.externalUserId ?? frozen.walletAddress,
      toUserId: live.config.externalUserId ?? live.walletAddress,
    })
  }

  return { identity: frozen ?? live, onFlowOpenChange }
}

// Compared by the fields that name the user, so a host rebuilding an equal
// config object each render is not a change.
function sameIdentity(a: IdentityProps, b: IdentityProps): boolean {
  return (
    a.config.apiKey === b.config.apiKey &&
    a.config.externalUserId === b.config.externalUserId &&
    a.walletAddress === b.walletAddress
  )
}
